import { useState } from 'react';
import { RefreshCw, ShieldCheck, ShieldX, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { useGetSubsidies, type SdMembership, type SdSubsidy } from '@/hooks/useSygeforsikring';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface SygeforsikringTabProps {
  /** The clinic this patient belongs to */
  clinicId: string;
  /** Decrypted CPR number — must only be passed after the user has explicitly
   *  unlocked it via the CPR reveal flow. Never stored; only kept in React
   *  state for the duration of this component's mount. */
  cpr: string | null;
  /** Called when the component needs the user to reveal CPR first */
  onRequestCprReveal?: () => void;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function MembershipBadge({ membership }: { membership: SdMembership }) {
  const { t } = useTranslation();
  if (membership.isMember) {
    return (
      <div className="flex items-center gap-3">
        <ShieldCheck className="h-8 w-8 shrink-0 text-green-500" />
        <div>
          <p className="font-medium text-gray-900 dark:text-white">{t('sd.membership_active')}</p>
          {membership.group && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('sd.group_label', { group: membership.group })}
            </p>
          )}
          {(membership.validFrom || membership.validTo) && (
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {membership.validFrom &&
                format(new Date(membership.validFrom), 'dd/MM/yyyy', { locale: da })}
              {membership.validFrom && membership.validTo && ' – '}
              {membership.validTo &&
                format(new Date(membership.validTo), 'dd/MM/yyyy', { locale: da })}
            </p>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3">
      <ShieldX className="h-8 w-8 shrink-0 text-gray-400" />
      <p className="text-gray-700 dark:text-gray-300">{t('sd.membership_inactive')}</p>
    </div>
  );
}

function SubsidyTable({ subsidies }: { subsidies: SdSubsidy[] }) {
  const { t } = useTranslation();
  if (subsidies.length === 0) return null;
  return (
    <div className="mt-6 border-t border-gray-200 pt-6 dark:border-gray-700">
      <h5 className="mb-3 text-sm font-medium text-gray-900 dark:text-white">
        {t('sd.subsidies_title')} ({subsidies.length})
      </h5>
      <div className="overflow-hidden rounded-lg shadow ring-1 ring-black ring-opacity-5">
        <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th
                scope="col"
                className="py-3 pl-4 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
              >
                {t('sd.col_treatment')}
              </th>
              <th
                scope="col"
                className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
              >
                {t('sd.col_amount')}
              </th>
              <th
                scope="col"
                className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
              >
                {t('sd.col_max_per_year')}
              </th>
              <th
                scope="col"
                className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
              >
                {t('sd.col_group')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
            {subsidies.map((s) => (
              <tr key={`${s.code}-${s.group}`} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                <td className="py-3 pl-4 pr-3 text-sm">
                  <p className="font-medium text-gray-900 dark:text-white">{s.name}</p>
                  <p className="text-xs text-gray-400">{s.code}</p>
                </td>
                <td className="px-3 py-3 text-sm text-gray-700 dark:text-gray-300">
                  {s.baseAmount > 0
                    ? s.baseAmount.toLocaleString('da-DK', { style: 'currency', currency: 'DKK' })
                    : '—'}
                </td>
                <td className="px-3 py-3 text-sm text-gray-700 dark:text-gray-300">
                  {s.maxPerYear != null ? `${s.maxPerYear}×` : t('sd.unlimited')}
                </td>
                <td className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
                  {s.group ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function SygeforsikringTab({ clinicId, cpr, onRequestCprReveal }: SygeforsikringTabProps) {
  const { t } = useTranslation();
  const getSubsidies = useGetSubsidies();

  const [membership, setMembership] = useState<SdMembership | null>(null);
  const [subsidies, setSubsidies] = useState<SdSubsidy[]>([]);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  const handleCheck = async () => {
    if (!cpr) {
      if (onRequestCprReveal) {
        onRequestCprReveal();
        toast(t('sd.reveal_cpr_first'));
      }
      return;
    }

    try {
      const result = await getSubsidies.mutateAsync({ clinic_id: clinicId, cpr });
      setMembership(result.membership);
      setSubsidies(result.subsidies);
      setLastChecked(new Date());

      if (result.membership.isMember) {
        toast.success(t('sd.toast_member_found', { count: result.subsidies.length }));
      } else {
        toast(t('sd.toast_not_member'));
      }
    } catch {
      toast.error(t('sd.toast_check_error'));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-medium text-gray-900 dark:text-white">
            {t('sd.section_title')}
          </h3>
          {lastChecked && (
            <p className="mt-0.5 text-xs text-gray-400">
              {t('sd.last_checked', {
                date: format(lastChecked, 'dd/MM/yyyy HH:mm', { locale: da }),
              })}
            </p>
          )}
        </div>
        <Button variant="secondary" onClick={handleCheck} disabled={getSubsidies.isPending}>
          {getSubsidies.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('sd.checking')}
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-4 w-4" />
              {cpr ? t('sd.check_membership') : t('sd.reveal_cpr_to_check')}
            </>
          )}
        </Button>
      </div>

      {/* Status card */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        {membership ? (
          <>
            <MembershipBadge membership={membership} />
            {membership.isMember && <SubsidyTable subsidies={subsidies} />}
          </>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {cpr ? t('sd.click_check_to_verify') : t('sd.reveal_cpr_to_check_desc')}
          </p>
        )}
      </div>
    </div>
  );
}
